import { useMemo } from "react";
import { Box, Stack, useMediaQuery, useTheme } from "@mui/material";
import { Button, FoodsIcon, Typography } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import {
    PackagingCompositionSchema,
    type PackagingChildRef,
    type PackagingComposition,
} from "src/api/client-api/packagings/useGetOnePackaging";
import { usePackagingReference } from "src/api/sure-api/usePackagingReference";
import { usePackagingReferenceComposite } from "src/api/sure-api/usePackagingReferenceComposite";
import { PageBackground } from "src/components/EntityForm/PageBackground";
import { SectionCard } from "src/components/EntityForm/SectionCard";
import { PackagingTypeTag, type PackagingTypeTagKind } from "src/components/Packaging/PackagingTypeTag";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import {
    InfoField,
    InformationDivider,
    InfoSection,
    productInformationNoValue as noValue,
} from "src/pages/Product/v2/ProductInformationsV2/ProductInformationDisplay";
import { URLHandler } from "src/services/URLHandler";
import { formatNumberByType } from "src/utils/formatting";
import { usePackagingContext } from "../PackagingContext";

type CompositionDisplayItem = {
    amount: number;
    children: CompositionDisplayItem[];
    id: string;
    kind: PackagingTypeTagKind;
    label: string;
    packagingId?: number;
};

type ReferenceComposition = {
    amount: number;
    isMain: boolean;
    label: string;
    packagingReferenceId: number;
    position: number;
};

const readNestedComposition = (child: PackagingChildRef): PackagingComposition[] => {
    const composition = child.composition;
    if (!Array.isArray(composition)) return [];

    return composition.flatMap((row): PackagingComposition[] => {
        const parsed = PackagingCompositionSchema.safeParse(row);
        return parsed.success ? [parsed.data] : [];
    });
};

const childKind = (child: PackagingChildRef): PackagingTypeTagKind =>
    child["@type"] === "PackagingComposite" || child.type === "composite" || readNestedComposition(child).length > 0 ?
        "composite"
    :   "element";

const projectTenantComposition = (composition: PackagingComposition[]): CompositionDisplayItem[] =>
    [...composition]
        .sort((left, right) => left.position - right.position)
        .map((row) => ({
            amount: row.amount,
            children: projectTenantComposition(readNestedComposition(row.child)),
            id: `packaging-${row.child.id}-${row.id}`,
            kind: childKind(row.child),
            label: row.child.name,
            packagingId: row.child.id,
        }));

const projectReferenceComposition = (composition: ReferenceComposition[]): CompositionDisplayItem[] =>
    [...composition]
        .sort((left, right) => left.position - right.position)
        .map((row) => ({
            amount: row.amount,
            children: [],
            id: `reference-${row.packagingReferenceId}-${row.position}`,
            kind: "element",
            label: row.label,
        }));

const splitMainItems = <T extends { isMain?: boolean }>(rows: T[]): { associated: T[]; main: T[] } => {
    const explicitMain = rows.filter((row) => row.isMain === true);
    if (explicitMain.length > 0) {
        return {
            main: explicitMain,
            associated: rows.filter((row) => row.isMain !== true),
        };
    }

    return { main: rows.slice(0, 1), associated: rows.slice(1) };
};

const BooleanValue = ({ value }: { value: boolean | null | undefined }) => {
    const { t } = useTranslation();
    const theme = useTheme();

    if (value === null || value === undefined) {
        return <Typography variant="body-regular">{noValue}</Typography>;
    }

    return (
        <Stack direction="row" alignItems="center" gap={theme.spacing(0.5)}>
            <FoodsIcon icon={value ? "check" : "close"} size={2} />
            <Typography variant="body-regular" sx={{ lineHeight: theme.spacing(2.75) }}>
                {value ? t("common.yes", "Yes") : t("common.no", "No")}
            </Typography>
        </Stack>
    );
};

const CompositionTypeIcon = ({ kind }: { kind: PackagingTypeTagKind }) => {
    const theme = useTheme();
    const isElement = kind === "element";
    const colors = theme.custom.dataviz.colorsWithExtra[isElement ? 13 : 12];

    return (
        <Box
            aria-hidden
            sx={{
                alignItems: "center",
                backgroundColor: colors.variant[100],
                borderRadius: theme.spacing(0.5),
                color: colors.main,
                display: "inline-flex",
                height: theme.spacing(3),
                justifyContent: "center",
                width: theme.spacing(2.75),
            }}
        >
            <FoodsIcon icon={isElement ? "box" : "boxes"} size={1} />
        </Box>
    );
};

type CompositionRowProps = {
    getHref?: (packagingId: number) => string | undefined;
    item: CompositionDisplayItem;
};

const CompositionRow = ({ getHref, item }: CompositionRowProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const href = item.packagingId === undefined ? undefined : getHref?.(item.packagingId);

    return (
        <Stack gap={item.children.length > 0 ? theme.spacing(0.5) : 0} minWidth={0}>
            <Stack direction="row" alignItems="center" gap={theme.spacing(1)} minWidth={0}>
                <CompositionTypeIcon kind={item.kind} />
                <Typography
                    variant="body-regular"
                    sx={{
                        color: theme.custom.grey[item.packagingId === undefined ? 2500 : 2800],
                        minWidth: 0,
                        overflowWrap: "anywhere",
                    }}
                >
                    {item.label} ({item.amount})
                </Typography>
                {href !== undefined && (
                    <Box
                        component="a"
                        aria-label={t("packaging.details.open", "Open packaging")}
                        href={href}
                        rel="noopener noreferrer"
                        target="_blank"
                        sx={{
                            alignItems: "center",
                            background: "transparent",
                            border: 0,
                            color: theme.custom.grey[2800],
                            cursor: "pointer",
                            display: "inline-flex",
                            padding: theme.spacing(0.5),
                        }}
                    >
                        <FoodsIcon icon="openNewTab" size={1} />
                    </Box>
                )}
            </Stack>
            {item.children.length > 0 && (
                <Stack
                    gap={theme.spacing(1)}
                    marginInlineStart={theme.spacing(1.5)}
                    paddingInlineStart={theme.spacing(2.5)}
                >
                    {item.children.map((child, index) => (
                        <Box
                            key={child.id}
                            data-composition-tree-branch
                            sx={{
                                position: "relative",
                                "&::before": {
                                    borderTopColor: theme.custom.grey[1800],
                                    borderTopStyle: theme.custom.strokes.containerDivider.style,
                                    borderTopWidth: theme.custom.strokes.containerDivider.width,
                                    content: '""',
                                    insetInlineStart: theme.spacing(-2.5),
                                    position: "absolute",
                                    top: theme.spacing(1.5),
                                    width: theme.spacing(1.5),
                                },
                                "&::after": {
                                    borderInlineStartColor: theme.custom.grey[1800],
                                    borderInlineStartStyle: theme.custom.strokes.containerDivider.style,
                                    borderInlineStartWidth: theme.custom.strokes.containerDivider.width,
                                    bottom:
                                        index === item.children.length - 1 ?
                                            `calc(100% - ${theme.spacing(1.5)})`
                                        :   theme.spacing(-0.5),
                                    content: '""',
                                    insetInlineStart: theme.spacing(-2.5),
                                    position: "absolute",
                                    top: index === 0 ? theme.spacing(-1) : theme.spacing(-0.5),
                                },
                            }}
                        >
                            <CompositionRow getHref={getHref} item={child} />
                        </Box>
                    ))}
                </Stack>
            )}
        </Stack>
    );
};

const CompositionList = ({
    getHref,
    items,
}: {
    getHref?: (id: number) => string | undefined;
    items: CompositionDisplayItem[];
}) => {
    const theme = useTheme();

    if (items.length === 0) {
        return <Typography variant="body-regular">{noValue}</Typography>;
    }

    return (
        <Stack gap={theme.spacing(1)}>
            {items.map((item) => (
                <CompositionRow key={item.id} getHref={getHref} item={item} />
            ))}
        </Stack>
    );
};

export const PackagingInformationsV2 = () => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const navigate = useNavigate();
    const { selectedCompany } = useFoodpilotContext();
    const { packaging } = usePackagingContext();
    const { packagingReferences } = usePackagingReference();
    const { packagingReferenceComposites } = usePackagingReferenceComposite();

    const elementReference = useMemo(
        () => packagingReferences.find((reference) => reference.id === packaging?.packagingReferenceId),
        [packaging?.packagingReferenceId, packagingReferences],
    );
    const compositeReference = useMemo(
        () =>
            packagingReferenceComposites.find((reference) => reference.id === packaging?.packagingReferenceCompositeId),
        [packaging?.packagingReferenceCompositeId, packagingReferenceComposites],
    );

    if (packaging === undefined) return null;

    const isComposite = packaging["@type"] === "PackagingComposite" || packaging.type === "composite";
    const referenceLabel = isComposite ? compositeReference?.label : undefined;
    const formattedWeight =
        packaging.weight == null ? noValue : formatNumberByType(packaging.weight, "grams", i18n.language, 3);
    const weightValue = formattedWeight === noValue ? noValue : `${formattedWeight}${t("weight.g", "g")}`;

    const tenantRows = packaging.composition ?? [];
    const referenceRows = compositeReference?.components ?? [];
    const tenantSplit = splitMainItems(tenantRows);
    const referenceSplit = splitMainItems(referenceRows);
    const mainItems =
        tenantRows.length > 0 ?
            projectTenantComposition(tenantSplit.main)
        :   projectReferenceComposition(referenceSplit.main);
    const associatedItems =
        tenantRows.length > 0 ?
            projectTenantComposition(tenantSplit.associated)
        :   projectReferenceComposition(referenceSplit.associated);

    const handleEdit = () => {
        if (selectedCompany === null) return;
        navigate(URLHandler.packaging.edit(selectedCompany.id, packaging.id, 0));
    };
    const getPackagingHref = (packagingId: number) =>
        selectedCompany === null ? undefined : URLHandler.packaging.information(selectedCompany.id, packagingId);

    return (
        <PageBackground>
            <Stack padding={theme.spacing(0, 0, 4)} gap={theme.spacing(3)}>
                <Stack
                    data-testid="packaging-information-header"
                    direction={isMobile ? "column" : "row"}
                    alignItems={isMobile ? "stretch" : "center"}
                    gap={theme.spacing(2)}
                >
                    <Stack
                        direction="row"
                        alignItems="center"
                        flex={1}
                        gap={theme.spacing(2)}
                        minWidth={0}
                        flexWrap="wrap"
                    >
                        <Typography variant="h3" noWrap={!isMobile}>
                            {packaging.name}
                        </Typography>
                        <PackagingTypeTag
                            appearance="details"
                            kind={isComposite ? "composite" : "element"}
                            label={
                                isComposite ?
                                    t("packaging.type.unit", "Unit")
                                :   t("packaging.type.component", "Component")
                            }
                        />
                    </Stack>
                    {packaging.canModify !== false && (
                        <Button
                            variant="primary"
                            onClick={handleEdit}
                            startIcon={<FoodsIcon icon="edit" size={2} />}
                            sx={{
                                borderRadius: theme.spacing(1),
                                boxShadow: theme.custom.shadows.hard,
                                gap: theme.spacing(1),
                                height: theme.spacing(5),
                                paddingInlineEnd: theme.spacing(2.5),
                                paddingInlineStart: theme.spacing(2),
                                width: isMobile ? "100%" : undefined,
                                "& .MuiButton-startIcon": { margin: theme.spacing(0) },
                            }}
                        >
                            {t("product.v2.informations.quick_edit", "Edit")}
                        </Button>
                    )}
                </Stack>

                <Box
                    data-testid="packaging-information-content"
                    sx={{
                        alignItems: "start",
                        display: "grid",
                        gap: theme.spacing(3),
                        gridTemplateColumns: isMobile ? "minmax(0, 1fr)" : "repeat(2, minmax(0, 1fr))",
                    }}
                >
                    <SectionCard data-testid="packaging-information-left-card">
                        <Stack padding={theme.spacing(3)} gap={theme.spacing(2)}>
                            <InfoSection
                                sectionKey="identity"
                                title={t("product.v2.informations.identity", "Identity")}
                                titleLineHeight={isComposite ? undefined : theme.spacing(3.125)}
                            >
                                <InfoField
                                    label={`${t("product.v2.identity.unique_identifier", "Unique identifier")}:`}
                                    lineHeight={isComposite ? undefined : theme.spacing(2.625)}
                                    value={packaging.externalId}
                                />
                                {isComposite && (
                                    <InfoField
                                        label={`${t(
                                            "packaging_composite.reference.label",
                                            "Associated reference packaging",
                                        )}:`}
                                        value={referenceLabel}
                                        tone="secondary"
                                    />
                                )}
                            </InfoSection>
                            {!isComposite && (
                                <>
                                    <InformationDivider />
                                    <InfoSection
                                        sectionKey="material"
                                        title={t("packaging.details.material", "Material")}
                                    >
                                        <InfoField
                                            label={`${t("packaging_element.material_type", "Type of material")}:`}
                                            value={elementReference?.label}
                                        />
                                        <InfoField
                                            label={`${t("glossary.weight", "Weight")}:`}
                                            value={weightValue}
                                            tone="secondary"
                                        />
                                    </InfoSection>
                                </>
                            )}
                        </Stack>
                    </SectionCard>

                    <SectionCard data-testid="packaging-information-right-card">
                        {isComposite ?
                            <Stack padding={theme.spacing(3)} gap={theme.spacing(3)}>
                                <InfoSection
                                    sectionKey="main-element"
                                    title={t("packaging.details.main_element", "Main element")}
                                >
                                    <CompositionList getHref={getPackagingHref} items={mainItems} />
                                </InfoSection>
                                <InformationDivider />
                                <InfoSection
                                    sectionKey="associated-elements"
                                    title={`${t("packaging.details.associated_elements", "Associated elements")} (${associatedItems.length})`}
                                    titleVariant="body-medium"
                                >
                                    <CompositionList getHref={getPackagingHref} items={associatedItems} />
                                </InfoSection>
                            </Stack>
                        :   <Stack padding={theme.spacing(3)}>
                                <InfoSection
                                    gap={theme.spacing(3)}
                                    sectionKey="environmental-properties"
                                    title={t("packaging.details.environmental_properties", "Environmental properties")}
                                >
                                    <InfoField
                                        label={`${t(
                                            "packaging_element.recycled_proportion",
                                            "Percentage of recycled raw material in this material",
                                        )}:`}
                                        value={
                                            packaging.recycledProportion == null ?
                                                noValue
                                            :   `${formatNumberByType(
                                                    packaging.recycledProportion,
                                                    "%",
                                                    i18n.language,
                                                    3,
                                                )} %`
                                        }
                                    />
                                    <InfoField
                                        label={`${t("packaging_element.recyclable", "Recyclable material")}:`}
                                        value={<BooleanValue value={packaging.recyclable} />}
                                    />
                                    <InfoField
                                        label={`${t("packaging_element.reuse", "Reusable material")}:`}
                                        value={<BooleanValue value={packaging.reuse} />}
                                    />
                                </InfoSection>
                            </Stack>
                        }
                    </SectionCard>
                </Box>
            </Stack>
        </PageBackground>
    );
};
